'use client';

import Image from 'next/image';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import useBreakpoints from '@/hooks/useBreakpoints';
import type { AdType } from '../../type';
import { AD_LOAD_TIMEOUT_MS, AD_SENSE_CLIENT_ID, MIN_WATCH_DURATION_BANNER, MIN_WATCH_DURATION_VIDEO } from '../constants';
import { useAdWatchTimer } from '../hooks/useAdWatchTimer';
import { ensureAdSenseScript } from '../utils';
import { AdFallbackPlaceholder } from './AdFallbackPlaceholder';
import { VideoWatchNotCompletedModal } from './modals';
type AdLoadStatus = 'loading' | 'loaded' | 'fallback';
interface AdOverlayProps {
  adType: AdType;
  googleAdsenseUnitId: string;
  onCompleted: (watchedDuration: number) => void;
  onDismissed: (watchedDuration: number) => void;
}

/**
 * @description Оверлей показа рекламы Google AdSense.
 * Гарантирует загрузку скрипта AdSense перед запросом рекламы.
 * Отслеживает реальную загрузку рекламы через MutationObserver.
 * Если AdSense не заполнил блок за AD_LOAD_TIMEOUT_MS — показывает fallback-заглушку,
 * чтобы поток можно было тестировать на staging/dev окружениях.
 */

export const AdOverlay: FC<AdOverlayProps> = ({
  adType,
  googleAdsenseUnitId,
  onCompleted,
  onDismissed
}) => {
  const {
    lg
  } = useBreakpoints();
  const adContainerRef = useRef<HTMLDivElement>(null);
  const insRef = useRef<HTMLModElement>(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [adLoadStatus, setAdLoadStatus] = useState<AdLoadStatus>('loading');
  const minDuration = adType === 'video' ? MIN_WATCH_DURATION_VIDEO : MIN_WATCH_DURATION_BANNER;
  const {
    elapsed,
    start,
    stop
  } = useAdWatchTimer();
  const isCompleted = elapsed >= minDuration;

  // Автоматически завершаем просмотр когда таймер достиг минимальной длительности
  useEffect(() => {
    if (!isCompleted) return;

    // Небольшая задержка, чтобы пользователь увидел прогресс-бар на 100%
    const timeout = setTimeout(() => {
      stop();
      onCompleted(elapsed);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [isCompleted, elapsed, stop, onCompleted]);

  /**
   * Загружает скрипт AdSense (если нужно), затем запрашивает заполнение рекламного блока.
   */
  const pushAd = useCallback(async () => {
    const scriptLoaded = await ensureAdSenseScript();
    if (!scriptLoaded) {
      setAdLoadStatus('fallback');
      return;
    }
    try {
      const w = window as Window & {
        adsbygoogle?: Record<string, unknown>[];
      };
      w.adsbygoogle = w.adsbygoogle || [];
      w.adsbygoogle.push({});
    } catch {
      // AdSense может не работать на localhost/dev
    }
  }, []);

  // Отслеживаем реальную загрузку рекламы через MutationObserver
  useEffect(() => {
    const insElement = insRef.current;
    if (!insElement) return;
    let resolved = false;
    const resolve = (status: AdLoadStatus) => {
      if (resolved) return;
      resolved = true;
      setAdLoadStatus(status);
    };

    // MutationObserver ждёт data-ad-status, который AdSense выставляет ПОСЛЕ вставки iframe.
    const observer = new MutationObserver(() => {
      const adStatus = insElement.getAttribute('data-ad-status');
      if (adStatus === 'unfilled') {
        resolve('fallback');
        observer.disconnect();
        return;
      }
      if (adStatus === 'filled') {
        resolve('loaded');
        observer.disconnect();
      }
    });
    observer.observe(insElement, {
      childList: true,
      subtree: true,
      attributes: true
    });

    // Проверяем, не загружен ли контент уже (если data-ad-status уже выставлен)
    const currentStatus = insElement.getAttribute('data-ad-status');
    if (currentStatus === 'filled') {
      resolve('loaded');
    } else if (currentStatus === 'unfilled') {
      resolve('fallback');
    }

    // Fallback — если реклама не загрузилась за отведённое время, показываем заглушку
    const fallbackTimer = setTimeout(() => {
      resolve('fallback');
    }, AD_LOAD_TIMEOUT_MS);
    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      pushAd();
      start();
    }, 300);
    return () => {
      clearTimeout(timer);
      stop();
    };
  }, [pushAd, start, stop]);
  const handleClose = useCallback(() => {
    if (isCompleted) {
      stop();
      onCompleted(elapsed);
    } else {
      setShowConfirmClose(true);
      stop();
    }
  }, [isCompleted, elapsed, stop, onCompleted]);
  const handleConfirmLeave = useCallback(() => {
    setShowConfirmClose(false);
    onDismissed(elapsed);
  }, [elapsed, onDismissed]);
  const handleCancelLeave = useCallback(() => {
    setShowConfirmClose(false);
    start();
  }, [start]);
  const progressPercent = Math.min(elapsed / minDuration * 100, 100);
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      {/* Close button */}
      <button type="button" onClick={handleClose} className={`absolute ${lg ? 'right-6 top-6' : 'right-4 top-4'} z-[10001] flex items-center justify-center rounded-full border border-[#E9ECF033] bg-[#1E1E2A]/80 p-2.5 transition-colors hover:bg-white/10`}>
        <Image src="/icons/close-default.svg" width={14} height={14} alt="Close" />
      </button>

      {/* Progress bar */}
      <div className="absolute left-0 top-0 z-[10000] h-1 w-full bg-white/10">
        <div className="h-full bg-gradient-to-r from-[#00b9ff] to-[#00ff88] transition-all duration-1000 ease-linear" style={{
        width: `${progressPercent}%`
      }} />
      </div>

      {/* Timer display */}
      {!isCompleted && <div className="absolute left-1/2 top-4 z-[10000] -translate-x-1/2 rounded-full bg-[#1E1E2A]/80 px-4 py-1.5">
          <span className="text-sm text-white/70">{Math.max(minDuration - elapsed, 0)}s</span>
        </div>}

      {/* Ad container */}
      <div ref={adContainerRef} className={`relative z-[10000] flex items-center justify-center ${lg ? 'h-[80vh] w-[80vw] max-w-[900px]' : 'h-full w-full'} overflow-hidden ${lg ? 'rounded-2xl' : ''} bg-[#1E1E2A]`}>
        {/* Real AdSense element — скрываем если показываем fallback */}
        <ins ref={insRef} className="adsbygoogle" style={{
        display: adLoadStatus === 'fallback' ? 'none' : 'block',
        width: '100%',
        height: '100%'
      }} data-ad-client={AD_SENSE_CLIENT_ID} data-ad-slot={googleAdsenseUnitId} data-ad-format={adType === 'video' ? 'fluid' : 'auto'} data-full-width-responsive="true" />

        {/* Loading spinner — пока ждём AdSense или fallback */}
        {adLoadStatus === 'loading' && <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-white/80" />
          </div>}

        {/* Fallback placeholder — когда AdSense не заполнил блок */}
        {adLoadStatus === 'fallback' && <AdFallbackPlaceholder adType={adType} />}
      </div>

      {/* Confirm close */}
      <VideoWatchNotCompletedModal isOpen={showConfirmClose} onClose={handleConfirmLeave} onCancel={handleConfirmLeave} onTryAgain={handleCancelLeave} zIndex={10003} />
    </div>;
};